import { DestroyRef, Injectable } from '@angular/core';
import {
  API,
  BookingDTO,
  BookingRecalculationDTO,
  CommercialFullDTO,
  PublisherBookingDTO,
  RequirementDTO,
  RequirementFullDTO,
} from './api/api';
import { ErrorService } from './error.service';
import { ToasterService, ToastType } from './toaster/toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';

@Injectable()
export class BookingService {
  constructor(
    private api: API,
    private errorHandler: ErrorService,
    private toastHandler: ToasterService,
    private translate: TranslateService,
    private destroyRef: DestroyRef
  ) {}

  public createRequirements(
    commercial: CommercialFullDTO,
    planningId?: number,
    requirements?: RequirementDTO[]
  ): Promise<RequirementFullDTO[]> {
    return new Promise(resolve => {
      if (!commercial.requirements || commercial.requirements.length === 0) {
        const obsArray: Observable<RequirementFullDTO>[] = [];
        let doneCounter: number = 0;
        do {
          const r: RequirementFullDTO = new RequirementFullDTO();
          r.name = requirements?.[doneCounter].name ?? commercial?.name ?? 'Requirement';
          r.active = true;
          r.timeCull = requirements?.[doneCounter].timeCull ?? null;
          r.playerCull = requirements?.[doneCounter].playerCull ?? null;
          r.geoCull = requirements?.[doneCounter].geoCull ?? null;
          if (planningId) {
            obsArray.push(this.api.ds_cm_Commercial_createRequirementPlanning(commercial.id, planningId, r));
          } else {
            obsArray.push(this.api.ds_cm_Commercial_createRequirement(commercial.id, r));
          }
          doneCounter++;
        } while (doneCounter < requirements?.length);
        forkJoin<RequirementFullDTO[]>(obsArray)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (createdRequirements: RequirementFullDTO[]) => {
              resolve(createdRequirements);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      } else {
        resolve(null);
      }
    });
  }

  public createBookings(requirements: RequirementFullDTO[], planningId?: number): Promise<Map<number, BookingDTO[]>> {
    return new Promise((resolve, reject) => {
      const resultMap: Map<number, BookingDTO[]> = new Map<number, BookingDTO[]>();
      const obsArray: Observable<BookingRecalculationDTO>[] = [];
      requirements.forEach(requirement => {
        if (planningId) {
          obsArray.push(this.api.ds_cm_Requirement_triggerRecalculateBookingsPlanning(requirement.id, planningId));
        } else {
          obsArray.push(this.api.ds_cm_Requirement_triggerRecalculateBookings(requirement.id));
        }
      });
      forkJoin(obsArray)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (data: BookingRecalculationDTO[]) => {
            data.forEach((bookingRecalculation, index) => {
              resultMap.set(index, this.handleBookingRefresh(requirements[index].bookings, bookingRecalculation));
            });
            resolve(resultMap);
          },
          error: error => {
            reject(error);
            this.errorHandler.errorHandler(error);
          },
        });
    });
  }

  handleBookingRefresh(currentBookings: BookingDTO[], data: BookingRecalculationDTO): BookingDTO[] {
    if (!currentBookings) {
      currentBookings = [];
    }
    if (data.created?.length > 0) {
      for (const e of data.created) {
        currentBookings.push(e);
      }
    }
    if (data.restored?.length > 0) {
      for (const e of data.restored) {
        this.filterDeprecated(currentBookings, e.id);
        currentBookings.push(e);
      }
    }
    if (data.deprecated?.length > 0) {
      for (const e of data.deprecated) {
        this.filterDeprecated(currentBookings, e.id);
        currentBookings.push(e);
      }
    }
    if (data.unmodified?.length > 0) {
      for (const e of data.unmodified) {
        this.filterDeprecated(currentBookings, e.id);
        currentBookings.push(e);
      }
    }
    return currentBookings;
  }

  public setMediumGroup(mediumGroupId: number, bookings: BookingDTO[], overrideOptions: any, planningId?: number): Promise<BookingDTO[]> {
    return new Promise(resolve => {
      if (planningId) {
        this.api
          .ds_cm_Booking_setMediumGroupPlanningPreview(planningId, mediumGroupId, bookings, {
            overrideAssigned: overrideOptions.overrideAssigned,
            overrideExcluded: overrideOptions.overrideExcluded,
            overrideDeprecated: overrideOptions.overrideDeprecated,
            excludeNotAssigned: overrideOptions.excludeNotAssigned,
          })
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (receivedBookings: BookingDTO[]) => {
              this.bookingToast(receivedBookings);
              resolve(receivedBookings);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      } else {
        this.api
          .ds_cm_Booking_setMediumGroupPreview(mediumGroupId, bookings, {
            overrideAssigned: overrideOptions.overrideAssigned,
            overrideExcluded: overrideOptions.overrideExcluded,
          })
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (receivedBookings: BookingDTO[]) => {
              this.bookingToast(receivedBookings);
              resolve(receivedBookings);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    });
  }

  public setMedium(mediumId: number, bookings: BookingDTO[], overrideOptions: any, planningId?: number): Promise<BookingDTO[]> {
    return new Promise(resolve => {
      if (planningId) {
        this.api
          .ds_cm_Booking_setMediumPlanningPreview(planningId, mediumId, bookings, {
            overrideAssigned: overrideOptions.overrideAssigned,
            overrideExcluded: overrideOptions.overrideExcluded,
            overrideDeprecated: overrideOptions.overrideDeprecated,
            excludeNotAssigned: overrideOptions.excludeNotAssigned,
          })
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (receivedBookings: BookingDTO[]) => {
              this.bookingToast(receivedBookings);
              resolve(receivedBookings);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      } else {
        this.api
          .ds_cm_Booking_setMediumPreview(mediumId, bookings, {
            overrideAssigned: overrideOptions.overrideAssigned,
            overrideExcluded: overrideOptions.overrideExcluded,
          })
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (receivedBookings: BookingDTO[]) => {
              this.bookingToast(receivedBookings);
              resolve(receivedBookings);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    });
  }

  public bookingToast(bookings: BookingDTO[]) {
    const countBooking = this.countFilledBookings(bookings);
    if (countBooking > 0) {
      this.toastHandler.toast(ToastType.SUCCESS, countBooking + ' ' + this.translate.instant('lang.globals.successfulBooking'));
    } else {
      this.toastHandler.toast(ToastType.ALERT, this.translate.instant('lang.globals.unsuccessfulBooking'));
    }
  }

  public editBookings(bookings: BookingDTO[], planningId?: number): Promise<BookingDTO[]> {
    return new Promise(resolve => {
      if (planningId) {
        this.api
          .ds_cm_Booking_editPlanningBookings(planningId, bookings)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: data => {
              resolve(data);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      } else {
        this.api
          .ds_cm_Booking_editBookings(bookings)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: data => {
              resolve(data);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
      return null;
    });
  }

  countDeprecatedBookings(bookings: BookingDTO[] | PublisherBookingDTO[]): number {
    if (bookings) {
      let bookingCount = 0;
      for (const booking of bookings) {
        if (booking.deprecated) {
          bookingCount++;
        }
      }
      return bookingCount;
    }
  }

  /* COUNT FUNCTIONS */

  countActiveBookings(bookings: BookingDTO[] | PublisherBookingDTO[]): number {
    if (bookings) {
      let bookingCount = 0;
      for (const booking of bookings) {
        if (!booking.deprecated && !booking.excluded) {
          bookingCount++;
        }
      }
      return bookingCount;
    } else {
      return 0;
    }
  }

  countMissingBookings(bookings: BookingDTO[] | PublisherBookingDTO[]): number {
    if (bookings) {
      let bookingCount = 0;
      for (const booking of bookings) {
        if (!booking.medium && !booking.excluded && !booking.deprecated) {
          bookingCount++;
        }
      }
      return bookingCount;
    } else {
      return 0;
    }
  }

  countExcludedBookings(bookings: BookingDTO[] | PublisherBookingDTO[]): number {
    if (bookings) {
      let bookingCount = 0;
      for (const booking of bookings) {
        if (booking.excluded) {
          bookingCount++;
        }
      }
      return bookingCount;
    } else {
      return 0;
    }
  }

  countFilledBookings(bookings: BookingDTO[] | PublisherBookingDTO[]): number {
    if (bookings) {
      let bookingCount = 0;
      for (const booking of bookings) {
        if (booking.medium && !booking.excluded) {
          bookingCount++;
        }
      }
      return bookingCount;
    } else {
      return 0;
    }
  }

  private filterDeprecated(previousBookings: BookingDTO[], searchVal: number) {
    if (previousBookings && previousBookings.length > 0) {
      const bookingForDelete = previousBookings.findIndex(x => x.id === searchVal);
      if (bookingForDelete !== -1) {
        previousBookings.splice(bookingForDelete, 1);
      }
    }
  }
}
